import { useTranslation } from "react-i18next";
import {
  Button,
  SearchBox,
  Dropdown,
  Option,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useRequestsPage, type RequestStatusFilter } from "./hooks/useRequestsPage";
import { telemetryService } from "./services/telemetry.service";
import {
  RequestsDataGrid,
  RequestDetailsDialog,
  CancelDialog,
  ErrorNotification,
  RequestsLegend,
} from "./components/requestsPage";
import "./styles/index.css";

interface RequestsPageProps {
  theme?: string;
}

const useStyles = makeStyles({
  root: {
    width: '100%',
    maxWidth: '100%',
    margin: 0,
    padding: tokens.spacingVerticalXL,
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    minHeight: '100vh',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  loadingSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  loadingText: {
    marginTop: tokens.spacingVerticalL,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    gap: tokens.spacingVerticalL,
  },
  errorMessage: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    textAlign: 'center',
    fontWeight: tokens.fontWeightMedium,
  },
  header: {
    marginBottom: tokens.spacingVerticalXXL,
    textAlign: 'left',
  },
  headerTitle: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    margin: `0 0 ${tokens.spacingVerticalS} 0`,
    color: tokens.colorNeutralForeground1,
  },
  headerSubtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    margin: 0,
    lineHeight: tokens.lineHeightBase300,
  },
  filtersSection: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingVerticalXL,
    marginBottom: tokens.spacingVerticalXL,
    boxShadow: tokens.shadow8,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  filtersContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  filterItem: {
    flex: 1,
    minWidth: '280px',
  },
  filterLabel: {
    display: 'block',
    marginBottom: tokens.spacingVerticalS,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },
  filtersSummaryText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    margin: 0,
  },
  filtersItemsPerPageLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  emptyState: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXL}`,
    textAlign: 'center',
    boxShadow: tokens.shadow16,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: tokens.spacingVerticalL,
    opacity: 0.6,
  },
  emptyTitle: {
    margin: `0 0 ${tokens.spacingVerticalM} 0`,
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
  },
  emptyMessage: {
    margin: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
});

export default function RequestsPage({ theme = "default" }: RequestsPageProps) {
  const styles = useStyles();
  const { t } = useTranslation('requestsPage');
  const page = useRequestsPage();

  if (page.loading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}>
            <Spinner size="large" />
          </div>
          <div className={styles.loadingText}>{t('messages.loading')}</div>
        </div>
      </div>
    );
  }

  if (page.error) {
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>{t('header.title')}</h1>
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>⚠️ {page.error}</div>
          <Button appearance="primary" onClick={page.initialize}>
            {t('common:buttons.retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>
          {page.isApprover ? t('header.titleApprover') : t('header.title')}
        </h1>
        <p className={styles.headerSubtitle}>
          {page.isApprover ? t('header.subtitleApprover') : t('header.subtitle')}
        </p>
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.filtersContainer}>
          <div className={styles.filterItem}>
            <label className={styles.filterLabel}>{t('filters.searchLabel')}</label>
            <SearchBox
              placeholder={t('filters.searchPlaceholder')}
              value={page.searchQuery}
              onChange={(_, data) => page.setSearchQuery(data?.value || "")}
              style={{ width: "100%" }}
            />
          </div>
          <div className={styles.filterItem}>
            <label className={styles.filterLabel}>{t('filters.statusLabel')}</label>
            <Dropdown
              placeholder={t('filters.statusPlaceholder')}
              value={page.statusFilter === "all" ? t('statusOptions.all') : page.statusFilter}
              onOptionSelect={(_, data) => page.setStatusFilter(data.optionValue as RequestStatusFilter)}
              style={{ minWidth: "180px" }}
            >
              <Option value="all">{t('statusOptions.all')}</Option>
              <Option value="Pending">{t('statusOptions.pending')}</Option>
              <Option value="Approved">{t('statusOptions.approved')}</Option>
              <Option value="CreatingTeam">{t('statusOptions.creatingTeam')}</Option>
              <Option value="Created">{t('statusOptions.created')}</Option>
              <Option value="Completed">{t('statusOptions.completed')}</Option>
              <Option value="Rejected">{t('statusOptions.rejected')}</Option>
              <Option value="Failed">{t('statusOptions.failed')}</Option>
              <Option value="Cancelled">{t('statusOptions.cancelled')}</Option>
            </Dropdown>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: tokens.spacingVerticalL }}>
          <p className={styles.filtersSummaryText}>
            {page.paginatedData.totalItems > 0
              ? t('pagination.display', { start: page.paginatedData.startIndex, end: page.paginatedData.endIndex, total: page.paginatedData.totalItems, plural: page.paginatedData.totalItems > 1 ? 's' : '' })
              : t('pagination.noResults')}
          </p>
          {page.paginatedData.totalItems > 0 && (
            <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'center' }}>
              <label className={styles.filtersItemsPerPageLabel}>
                {t('pagination.itemsPerPage')}
              </label>
              <Dropdown
                value={String(page.itemsPerPage)}
                onOptionSelect={(_, data) => page.handleItemsPerPageChange(Number(data.optionValue))}
                style={{ minWidth: '80px' }}
              >
                <Option value="10">10</Option>
                <Option value="25">25</Option>
                <Option value="50">50</Option>
                <Option value="100">100</Option>
              </Dropdown>
            </div>
          )}
        </div>
      </div>

      {page.paginatedData.items.length > 0 ? (
        <>
          <RequestsDataGrid
            items={page.paginatedData.items}
            isApprover={page.isApprover}
            currentUserId={page.currentUserId}
            getApprovalProgress={page.getApprovalProgress}
            currentPage={page.currentPage}
            totalPages={page.paginatedData.totalPages}
            onPageChange={page.handlePageChange}
            onSelectRequest={(item) => {
              telemetryService.capture('requests_detail_opened', page.currentUserId, {
                properties: {
                  request_id: item.id || '',
                  request_status: item.status,
                }
              });
              page.setSelectedRequest(item);
              page.setApprovalAction(null);
            }}
            onCancelRequest={page.handleCancelClick}
          />
          <RequestsLegend />
        </>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📭</div>
          <h3 className={styles.emptyTitle}>{t('emptyState.title')}</h3>
          <p className={styles.emptyMessage}>
            {page.searchQuery || page.statusFilter !== "all"
              ? t('emptyState.messageWithFilters')
              : page.isApprover
                ? t('emptyState.messageNoRequests')
                : t('emptyState.messageNotCreated')}
          </p>
        </div>
      )}

      <RequestDetailsDialog
        selectedRequest={page.selectedRequest}
        approvalAction={page.approvalAction}
        comments={page.comments}
        processingAction={page.processingAction}
        isApprover={page.isApprover}
        currentUserId={page.currentUserId}
        quotaModels={page.quotaModels}
        settings={page.settings}
        onClose={() => {
          page.setSelectedRequest(null);
          page.setApprovalAction(null);
          page.setComments("");
        }}
        onApprove={page.handleApprove}
        onReject={page.handleReject}
        onCancelClick={page.handleCancelClick}
        onCommentsChange={page.setComments}
      />

      <CancelDialog
        open={page.cancelDialogOpen}
        processing={page.processingAction !== null}
        onConfirm={page.handleCancelConfirm}
        onClose={page.handleCancelDialogClose}
      />

      <ErrorNotification
        isOpen={page.errorNotification.isOpen}
        title={page.errorNotification.title}
        message={page.errorNotification.message}
        onClose={page.closeErrorNotification}
      />
    </div>
  );
}
